import { Injectable } from '@angular/core';
import {
  EngagementMedia,
  EntityType,
  NewsfeedItemResponseDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { List } from '@epicuro-next/models/engagement';
import {
  selectListEntityFactory,
  updateEntityList,
} from '@epicuro-next/state/list-entities';
import { actions as feedActions } from '@epicuro-next/state/news-feed-state';
import { getListFeedIdPropertyByFeedType } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';

import {
  updateCommercialListSuccess,
  updateProductListSuccess,
} from '../../actions';
import { updateListNewsFeedId } from '../../actions/list/news-feed.actions';

/**
 * Product List Feed Effects
 */
@Injectable()
export class FeedEffects {
  public deleteArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.deleteArticleSuccess),
      filter(({ referenceType }) => referenceType === EntityType.ProductList),
      map(({ referenceId, newsfeedType }) =>
        updateListNewsFeedId({
          listId: referenceId,
          newsfeedId: undefined,
          newsfeedType,
        }),
      ),
    ),
  );

  public createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.createArticleSuccess),
      filter(({ article }) => article.type === EntityType.ProductList),
      map(({ article }) =>
        updateListNewsFeedId({
          listId: article.referenceId,
          newsfeedId: article.id,
          newsfeedType: article.newsfeedType,
        }),
      ),
    ),
  );

  public updateListEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateListNewsFeedId),
      concatLatestFrom(({ listId }) => [
        this.store.pipe(select(selectListEntityFactory(listId as string))),
      ]),
      filter(([, listEntity]) => !!listEntity?.list),
      map(([{ newsfeedId, newsfeedType }, listEntity]) =>
        updateEntityList({
          list: {
            ...listEntity?.list,
            ...{
              [getListFeedIdPropertyByFeedType(newsfeedType)]: newsfeedId,
            },
          } as List,
        }),
      ),
    ),
  );

  public updateProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProductListSuccess, updateCommercialListSuccess),
      filter(({ list }) => Boolean(list.newsfeedId)),
      map(({ list }) =>
        feedActions.partialUpdate({
          article: {
            title: list.name,
            id: list.newsfeedId as string,
            references: [
              {
                id: list.id,
                name: list.name,
                media: list.mainMedia as unknown as EngagementMedia,
              } as NewsfeedItemResponseDto & { media: EngagementMedia },
            ],
          },
        }),
      ),
    ),
  );

  /**
   * Dependencies
   * @param actions$ required by ngRx
   * @param store the ProductList store
   */
  constructor(private actions$: Actions, private store: Store) {}
}
